import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useMemo, useState } from "react";
import { Box, Text, Image, Button, Icon, Flex, IconButton, } from "@chakra-ui/react";
import { FiUser } from "react-icons/fi";
import ImageSlideshowColumn from "./ImageSlideshowColumn";
import { ArrowForwardIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { keyframes } from "@emotion/react";
import { useCustomerOptional } from "../../contexts/CustomerProvider";
// Constants
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "avi"];
const TRANSITION_DURATION = "1000ms";
const TRANSITION_EASING = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
// Helper function to check if URL is a video
const isVideo = (url) => {
    var _a;
    if (!url)
        return false;
    const extension = (_a = url.split(".").pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    return extension ? VIDEO_EXTENSIONS.includes(extension) : false;
};
// Flip animation for event cards
const flipCard = keyframes `
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(90deg);
  }
  100% {
    transform: rotateY(0deg);
  }
`;
const DEFAULT_COLORS = {
    primary: "#8155FE",
    text: "white",
    bg: "#111111",
    card_bg: "#16171C",
    card_border: "#2B2B2B",
};
export default function VenueHero({ pubDetail, venueName, tagline, onOpen, eventsData, pubId, pubColors, appContext, experienceProfileSlot, hideReserveCTA = false, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const navigate = useNavigate();
    const customer = useCustomerOptional();
    const isLoggedIn = !!((_a = customer === null || customer === void 0 ? void 0 : customer.customerData) === null || _a === void 0 ? void 0 : _a.id);
    const colors = {
        primary: (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _b !== void 0 ? _b : DEFAULT_COLORS.primary,
        text: (_c = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _c !== void 0 ? _c : DEFAULT_COLORS.text,
        bg: (_d = pubColors === null || pubColors === void 0 ? void 0 : pubColors.bg) !== null && _d !== void 0 ? _d : DEFAULT_COLORS.bg,
        card_bg: (_e = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_bg) !== null && _e !== void 0 ? _e : DEFAULT_COLORS.card_bg,
        card_border: (_f = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_border) !== null && _f !== void 0 ? _f : DEFAULT_COLORS.card_border,
    };
    const layerRefs = useRef([]);
    const videoRefs = useRef([]);
    const stickyContainerRef = useRef(null);
    const scrollAnimationFrameRef = useRef(null);
    const resizeTimeoutRef = useRef(null);
    const initialAnimationDoneRef = useRef(false);
    const [currentEventIndex, setCurrentEventIndex] = useState(0);
    const [readyVideoIndices, setReadyVideoIndices] = useState(new Set());
    const eventFlipIntervalRef = useRef(null);
    const primaryVideoUrl = (_g = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _g === void 0 ? void 0 : _g.video;
    const primaryVideoThumbnail = (_h = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _h === void 0 ? void 0 : _h.videoThumbnail;
    // Get events list
    const events = (eventsData === null || eventsData === void 0 ? void 0 : eventsData.data) || [];
    // Format event date
    const formatEventDate = (eventDate) => {
        if (!eventDate || eventDate.length < 3)
            return "";
        const date = new Date(`${eventDate[0]}/${eventDate[1]}/${eventDate[2]}`);
        return format(date, "d MMM");
    };
    // Handle back navigation
    const goBack = () => {
        if (!window.history || window.history.length <= 1) {
            navigate("/home");
        }
        else {
            navigate(-1);
        }
    };
    // Reset video-ready state when the pub (and therefore its videos) changes
    useEffect(() => {
        setReadyVideoIndices(new Set());
    }, [pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.uuid, (_j = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _j === void 0 ? void 0 : _j.video]);
    // Auto-flip through events
    useEffect(() => {
        if (events.length <= 1)
            return;
        eventFlipIntervalRef.current = setInterval(() => {
            setCurrentEventIndex((prev) => (prev + 1) % events.length);
        }, 4000); // Flip every 4 seconds
        return () => {
            if (eventFlipIntervalRef.current) {
                clearInterval(eventFlipIntervalRef.current);
            }
        };
    }, [events.length]);
    const { columns, totalColumns } = useMemo(() => {
        var _a;
        // Prepare media: Separate videos and images
        if (!pubDetail) {
            return { columns: [], totalColumns: 0 };
        }
        const videos = [];
        const images = [];
        // Collect all media
        const allMedia = [];
        if ((_a = pubDetail.metaData) === null || _a === void 0 ? void 0 : _a.video)
            allMedia.push(pubDetail.metaData.video);
        if (Array.isArray(pubDetail.vibes))
            allMedia.push(...pubDetail.vibes);
        if (Array.isArray(pubDetail.pub_images))
            allMedia.push(...pubDetail.pub_images);
        // Separate videos and images
        allMedia.forEach((url) => {
            if (isVideo(url)) {
                videos.push(url);
            }
            else {
                images.push(url);
            }
        });
        // Calculate total assets
        const totalAssets = videos.length + images.length;
        let cols = [];
        let colsCount = 0;
        if (totalAssets >= 4) {
            // Always show 4 columns
            colsCount = 4;
            if (videos.length >= 4) {
                // Show 4 videos only
                cols = videos.slice(0, 4).map((video) => ({
                    type: "video",
                    content: [video],
                }));
            }
            else {
                // Fill with images first (left), then videos (right)
                const videosToShow = videos.slice(0, videos.length);
                const imageColumnsNeeded = colsCount - videosToShow.length;
                // Add image columns first (left side)
                for (let i = 0; i < imageColumnsNeeded && images.length > 0; i++) {
                    cols.push({
                        type: "image",
                        content: images, // All images for slideshow
                    });
                }
                // Add video columns (right side)
                videosToShow.forEach((video) => {
                    cols.push({
                        type: "video",
                        content: [video],
                    });
                });
            }
        }
        else {
            // Show fewer columns based on available assets
            // Add images first (left), then videos (right)
            if (images.length > 0) {
                cols.push({
                    type: "image",
                    content: images,
                });
            }
            videos.forEach((video) => {
                cols.push({
                    type: "video",
                    content: [video],
                });
            });
            colsCount = cols.length;
        }
        return { columns: cols, totalColumns: colsCount };
    }, [pubDetail]);
    // Parallax mouse movement effect for full-screen layered images
    useEffect(() => {
        // Reset animation flag when venue changes
        initialAnimationDoneRef.current = false;
        if (!pubDetail || totalColumns === 0)
            return;
        // Initialize layers with GPU acceleration properties
        const initializeLayers = () => {
            layerRefs.current.forEach((layer) => {
                if (layer) {
                    layer.style.willChange = "transform";
                    layer.style.backfaceVisibility = "hidden";
                    layer.style.perspective = "1000px";
                }
            });
        };
        // Initial animation: columns come from right to left
        const playInitialAnimation = () => {
            if (initialAnimationDoneRef.current)
                return;
            const windowWidth = window.innerWidth;
            const baseOffset = windowWidth * 0.3; // Start 30% off screen to the right
            layerRefs.current.forEach((layer, index) => {
                if (!layer)
                    return;
                // Each column starts further to the right, creating a staggered effect
                const startOffset = baseOffset + index * 50; // Stagger each column
                // Set initial position (off-screen to the right)
                layer.style.transition = "none";
                layer.style.transform = `translate3d(${startOffset}px, 0px, 0px)`;
                // Force reflow to ensure initial position is applied
                void layer.offsetHeight;
                // Animate to center position with staggered delay
                setTimeout(() => {
                    layer.style.transition = `transform ${TRANSITION_DURATION} ${TRANSITION_EASING}`;
                    layer.style.transform = "translate3d(0px, 0px, 0px)";
                }, 100 + index * 150); // Stagger delay: 100ms, 250ms, 400ms, 550ms
            });
            initialAnimationDoneRef.current = true;
        };
        // Handle parallax mouse movement effect using requestAnimationFrame for smooth 60fps
        const handleParallaxMouse = (e) => {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            // Calculate mouse position relative to center of screen (-1 to 1)
            const mouseX = (e.clientX / windowWidth) * 2 - 1; // -1 (left) to 1 (right)
            const mouseY = (e.clientY / windowHeight) * 2 - 1; // -1 (top) to 1 (bottom)
            layerRefs.current.forEach((layer, index) => {
                if (!layer)
                    return;
                // Calculate parallax speed - each column moves at different rate
                // All columns move, but at different speeds creating depth effect
                // First column moves slowest, last column moves fastest
                const speedMultiplier = 30; // Movement intensity
                const baseSpeed = 5; // Base speed so even first column moves
                const speed = baseSpeed + (index / Math.max(totalColumns - 1, 1)) * speedMultiplier;
                // Apply parallax: columns move based on mouse position
                // Mouse on right = columns move right, mouse on left = columns move left
                const translateX = mouseX * speed;
                const translateY = mouseY * speed * 0.5; // Vertical movement is less pronounced
                layer.style.transition = "none";
                layer.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px)`;
            });
        };
        // Smooth mouse move handler using requestAnimationFrame
        const onMouseMove = (e) => {
            // Cancel any pending animation frame
            if (scrollAnimationFrameRef.current !== null) {
                cancelAnimationFrame(scrollAnimationFrameRef.current);
            }
            // Schedule parallax handling in next animation frame for smooth 60fps
            scrollAnimationFrameRef.current = requestAnimationFrame(() => {
                handleParallaxMouse(e);
                scrollAnimationFrameRef.current = null;
            });
        };
        // Reset parallax when mouse leaves
        const onMouseLeave = () => {
            layerRefs.current.forEach((layer) => {
                if (layer) {
                    layer.style.transition = "transform 0.5s ease-out";
                    layer.style.transform = "translate3d(0px, 0px, 0px)";
                }
            });
        };
        // Wait for refs to be populated before initializing
        const initialize = () => {
            const allRefsReady = layerRefs.current.every((ref, index) => ref !== null || index >= totalColumns);
            if (!allRefsReady && layerRefs.current.length < totalColumns) {
                // Re-check after a short delay
                requestAnimationFrame(initialize);
                return;
            }
            // Initialize layers once refs are ready
            initializeLayers();
            // Play initial animation: columns come from right to left
            setTimeout(() => {
                playInitialAnimation();
            }, 50); // Small delay to ensure DOM is ready
            // Add mouse event listeners to sticky container once it's ready
            const stickyContainer = stickyContainerRef.current;
            if (stickyContainer) {
                stickyContainer.addEventListener("mousemove", onMouseMove, {
                    passive: true,
                });
                stickyContainer.addEventListener("mouseleave", onMouseLeave, {
                    passive: true,
                });
            }
            else {
                // Retry if sticky container not ready yet
                requestAnimationFrame(initialize);
            }
        };
        // Start initialization
        initialize();
        // Handle resize with debounce
        const handleResize = () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            resizeTimeoutRef.current = setTimeout(() => {
                // Reset on resize
                layerRefs.current.forEach((layer) => {
                    if (layer) {
                        layer.style.transform = "translate3d(0px, 0px, 0px)";
                    }
                });
            }, 150);
        };
        window.addEventListener("resize", handleResize, { passive: true });
        return () => {
            const stickyContainer = stickyContainerRef.current;
            if (stickyContainer) {
                stickyContainer.removeEventListener("mousemove", onMouseMove);
                stickyContainer.removeEventListener("mouseleave", onMouseLeave);
            }
            window.removeEventListener("resize", handleResize);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            if (scrollAnimationFrameRef.current !== null) {
                cancelAnimationFrame(scrollAnimationFrameRef.current);
            }
        };
    }, [pubDetail, totalColumns]);
    // Handle video autoplay for all videos
    useEffect(() => {
        videoRefs.current.forEach((videoElement, index) => {
            if (!videoElement)
                return;
            const timer = setTimeout(() => {
                videoElement.play().catch(() => {
                    // Silently handle autoplay restrictions
                });
            }, 1000 + index * 500); // Stagger video playback
            return () => clearTimeout(timer);
        });
    }, [columns]);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { position: "relative", w: "100%", h: "100vh", zIndex: 0, children: [_jsx(Box, { ref: stickyContainerRef, position: "relative", w: "100%", h: "100vh", overflow: "hidden", display: "flex", children: totalColumns === 0 ? (
                        // Fallback: If no media, show placeholder
                        _jsx(Box, { ref: (el) => (layerRefs.current[0] = el), w: "100%", h: "100vh", bg: "gray.800" })) : (_jsx(_Fragment, { children: columns.map((column, columnIndex) => {
                                const columnWidth = `${100 / totalColumns}%`;
                                if (column.type === "video") {
                                    // Calculate video index by counting videos before this column
                                    const videoIndex = columns
                                        .slice(0, columnIndex)
                                        .filter((col) => col.type === "video").length;
                                    return (_jsxs(Box, { ref: (el) => (layerRefs.current[columnIndex] = el), position: "relative", w: columnWidth, h: "100vh", overflow: "hidden", flexShrink: 0, children: [_jsx("video", { ref: (el) => {
                                                    if (el)
                                                        videoRefs.current[videoIndex] = el;
                                                }, src: column.content[0], muted: true, loop: true, playsInline: true, autoPlay: true, onCanPlay: () => setReadyVideoIndices((prev) => {
                                                    if (prev.has(videoIndex))
                                                        return prev;
                                                    const next = new Set(prev);
                                                    next.add(videoIndex);
                                                    return next;
                                                }), style: {
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                } }), column.content[0] === primaryVideoUrl &&
                                                primaryVideoThumbnail && (_jsx(Image, { src: primaryVideoThumbnail, alt: "", position: "absolute", top: 0, left: 0, w: "100%", h: "100%", objectFit: "cover", pointerEvents: "none", opacity: readyVideoIndices.has(videoIndex) ? 0 : 1, transition: "opacity 0.3s ease-out" }))] }, `video-${columnIndex}`));
                                }
                                else {
                                    // Image slideshow column - use separate component
                                    return (_jsx(ImageSlideshowColumn, { images: column.content, columnIndex: columnIndex, columnWidth: columnWidth, onRefReady: (el) => {
                                            layerRefs.current[columnIndex] = el;
                                        } }, `image-${columnIndex}`));
                                }
                            }) })) }), _jsx(Box, { display: "flex", alignItems: "center", position: "absolute", left: 0, bottom: 0, zIndex: 2, children: _jsxs(Box, { px: [6, 8, 12, 20], pb: 20, pointerEvents: "auto", position: "relative", w: "100%", h: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", children: [_jsx(Text, { fontSize: "6xl", fontWeight: 600, color: "white", mb: 2, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "system-ui, -apple-system, sans-serif", textShadow: "2px 2px 4px rgba(0,0,0,0.8)", children: venueName }), _jsxs(Text, { fontSize: "xl", color: "white", opacity: 0.9, fontWeight: 300, letterSpacing: "0.1em", textTransform: "uppercase", maxW: "600px", textShadow: "1px 1px 3px rgba(0,0,0,0.8)", children: ["- ", tagline] })] }) }), _jsx(Box, { position: "absolute", bottom: 0, left: 0, right: 0, h: "200px", background: `linear-gradient(180deg, rgba(17, 17, 17, 0) 6.16%, ${colors.bg} 98.48%)`, zIndex: 1 })] }), _jsx(Box, { position: "absolute", top: 0, left: 0, right: 0, zIndex: 1000, px: 5, py: 5, pointerEvents: "auto", children: _jsxs(Flex, { alignItems: "center", justifyContent: "space-between", w: "100%", maxW: "100%", children: [_jsxs(Flex, { alignItems: "center", gap: 3, children: [_jsx(IconButton, { "aria-label": "Go back", icon: _jsx(ChevronLeftIcon, { boxSize: 6 }), onClick: goBack, bg: colors.card_bg, color: colors.text, borderRadius: "10", size: "md", transition: "all 0.3s", border: `1px solid ${colors.card_border}`, _hover: {
                                        bg: colors.primary,
                                    } }), (pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.logo) && (_jsx(Image, { src: pubDetail.logo, alt: venueName || "Venue logo", w: "80px", maxH: "100px", objectFit: "contain", borderRadius: "8px" }))] }), _jsxs(Flex, { alignItems: "center", gap: 4, children: [!hideReserveCTA && (_jsx(Box, { bg: `linear-gradient(90deg, ${colors.text} 0%, ${colors.card_bg} 100%)`, p: "2px", borderRadius: "md", _hover: {
                                        transform: "scale(1.05)",
                                    }, transition: "all 0.3s", boxShadow: `0px 0px 10px 0px ${colors.primary}`, children: _jsx(Button, { bg: colors.card_bg, color: colors.text, border: "none", borderRadius: "md", size: "md", px: 5, fontSize: "sm", fontWeight: 400, _hover: {
                                            bg: colors.primary,
                                        }, transition: "all 0.3s", onClick: onOpen, leftIcon: _jsx(Icon, { as: ArrowForwardIcon, boxSize: 5, transform: "rotate(-45deg)" }), h: "55px", children: "Get Reservation" }) })), appContext === "experience" ? (_jsx(IconButton, { "aria-label": isLoggedIn ? "Profile" : "Sign in", icon: _jsx(Icon, { as: FiUser, boxSize: 5 }), onClick: () => {
                                        if (isLoggedIn) {
                                            navigate("/account");
                                        }
                                        else {
                                            const currentPath = window.location.pathname + window.location.search;
                                            navigate(`/verify-phone/direct?redirect=${encodeURIComponent(currentPath)}`);
                                        }
                                    }, bg: colors.card_bg, color: colors.text, border: `1px solid ${colors.card_border}`, borderRadius: "full", size: "md", _hover: { bg: colors.primary }, _active: { bg: colors.primary } })) : (experienceProfileSlot !== null && experienceProfileSlot !== void 0 ? experienceProfileSlot : null), events.length > 0 && (_jsx(Flex, { alignItems: "center", gap: 4, flex: 1, justifyContent: "center", mx: 4, children: _jsx(Box, { position: "relative", minW: "280px", h: "60px", style: { perspective: "1000px" }, children: events.map((event, index) => {
                                            const eventDate = event.event_st
                                                ? formatEventDate(event.event_st)
                                                : "";
                                            const isActive = index === currentEventIndex;
                                            // Format date for URL
                                            let dateString = "";
                                            if (event.event_st && event.event_st.length >= 3 && pubId) {
                                                const date = new Date(`${event.event_st[0]}/${event.event_st[1]}/${event.event_st[2]}`);
                                                dateString = format(date, "yyyy-MM-dd");
                                            }
                                            const eventUrl = pubId && event.uuid && dateString
                                                ? `/pub/detail/events/${pubId}?date=${dateString}&id=${event.uuid}`
                                                : event.uuid
                                                    ? `/event/detail/${event.uuid}`
                                                    : "#";
                                            return (_jsx(Link, { to: eventUrl, children: _jsx(Box, { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, opacity: isActive ? 1 : 0, transform: isActive ? "rotateY(0deg)" : "rotateY(90deg)", transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out", pointerEvents: isActive ? "auto" : "none", bg: colors.card_bg, borderRadius: "md", p: 3, border: `1px solid ${colors.card_border}`, backdropFilter: "blur(10px)", children: _jsxs(Flex, { alignItems: "center", justifyContent: "space-between", h: "100%", gap: 3, children: [_jsx(Icon, { as: ArrowForwardIcon, color: colors.text, boxSize: 5, transform: "rotate(-45deg)" }), _jsxs(Flex, { flexDirection: "column", alignItems: "flex-start", flex: 1, gap: 0, children: [event.event_type && (_jsx(Text, { fontSize: "xs", fontWeight: "400", color: colors.text, children: event.event_type.toUpperCase() })), _jsx(Text, { fontWeight: "400", color: colors.text, fontSize: "sm", noOfLines: 1, children: event.event_name })] }), eventDate && (_jsx(Text, { fontSize: "xl", fontWeight: "700", color: colors.text, children: eventDate }))] }) }) }, event.uuid));
                                        }) }) }))] })] }) })] }));
}
